// Provider adapter registry (frontend).
//
// Core commerce code never branches on `provider`. Provider-specific display
// metadata and capability flags live here, behind a single boundary, and the
// UI reads from this registry. The authoritative API behaviour lives in the
// backend (RPCs) and the edge-function adapters.

export interface ProviderAdapterMeta {
  provider: string;
  label: string;
  icon: string;
  description: string;
  capabilities: Record<string, boolean>;
  /** Marketplace sites, where relevant (e.g. eBay). Empty for single-region providers. */
  marketplaceSites: { value: string; label: string }[];
  /** Whether the provider supports multiple independent store/account instances. */
  multiInstance: boolean;
}

export const providerRegistry: ProviderAdapterMeta[] = [
  {
    provider: "bigcommerce",
    label: "BigCommerce",
    icon: "ri-shopping-bag-3-line",
    description: "Connect a BigCommerce store to import orders and publish inventory.",
    capabilities: {
      import_products: true,
      import_orders: true,
      publish_inventory: true,
      publish_fulfillment: true,
      webhooks: true,
      auth_oauth: true,
    },
    marketplaceSites: [],
    multiInstance: true,
  },
  {
    provider: "ebay",
    label: "eBay",
    icon: "ri-auction-line",
    description: "Connect one or more independent eBay seller accounts/shops.",
    capabilities: {
      import_products: true,
      import_orders: true,
      publish_inventory: true,
      publish_fulfillment: true,
      webhooks: true,
      notifications: true,
      auth_oauth: true,
    },
    marketplaceSites: [
      { value: "EBAY_US", label: "United States (EBAY_US)" },
      { value: "EBAY_GB", label: "United Kingdom (EBAY_GB)" },
      { value: "EBAY_AU", label: "Australia (EBAY_AU)" },
      { value: "EBAY_DE", label: "Germany (EBAY_DE)" },
      { value: "EBAY_CA", label: "Canada (EBAY_CA)" },
      { value: "EBAY_FR", label: "France (EBAY_FR)" },
      { value: "EBAY_IT", label: "Italy (EBAY_IT)" },
      { value: "EBAY_ES", label: "Spain (EBAY_ES)" },
    ],
    multiInstance: true,
  },
  {
    provider: "shopify",
    label: "Shopify",
    icon: "ri-shopping-bag-line",
    description: "Connect a Shopify store (future target).",
    capabilities: { import_orders: true, publish_inventory: true, publish_fulfillment: true, webhooks: true, auth_oauth: true },
    marketplaceSites: [],
    multiInstance: true,
  },
  {
    provider: "amazon",
    label: "Amazon",
    icon: "ri-store-3-line",
    description: "Amazon Seller Central uses the same Connect → authorize → return model when this channel is enabled.",
    capabilities: { auth_oauth: true, import_orders: false, publish_inventory: false, publish_fulfillment: false },
    marketplaceSites: [
      { value: "AU", label: "Australia" },
      { value: "US", label: "United States" },
      { value: "UK", label: "United Kingdom" },
    ],
    multiInstance: true,
  },
];

export function providerMeta(provider: string | null | undefined): ProviderAdapterMeta | undefined {
  if (!provider) return undefined;
  return providerRegistry.find((p) => p.provider === provider);
}

export function marketplaceSiteLabel(site: string | null | undefined): string {
  if (!site) return "";
  for (const p of providerRegistry) {
    const found = p.marketplaceSites.find((s) => s.value === site);
    if (found) return found.label;
  }
  return site;
}